/**
 * WebhookHealth — At-a-glance dashboard of the buyer's webhook delivery health.
 *
 * Shows:
 *   - 4 KPI tiles: success %, p50 latency, p95 latency, attempts (24h or all-time)
 *   - 14-day daily sparkline (success-rate over time)
 *   - Status-code breakdown (200/201/400/500/timeout)
 *   - Last 5 failures with reason + timestamp
 *
 * Data source: GET /api/client/delivery/health
 * Mounted at the top of /dashboard/delivery.
 *
 * Designed to be the single page a buyer actually opens when something feels off
 * with their CRM delivery. Goal: < 2 seconds to understand "is the webhook OK?"
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Activity, CheckCircle2, AlertTriangle, Clock, BarChart2, RefreshCw, Loader2, Zap,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const WINDOWS = [
  { id: "recent_24h", label: "Last 24h" },
  { id: "overall",    label: "All time" },
];

export default function WebhookHealth() {
  const [data, setData]   = useState(null);
  const [win, setWin]     = useState("recent_24h");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.get("/client/delivery/health");
      setData(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stats   = data?.[win] || null;
  const daily   = data?.daily || [];
  const hasData = !!stats?.total;

  if (busy && !data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <Loader2 className="size-5 animate-spin text-slate-400 mx-auto" />
        <div className="mt-2 text-sm text-slate-500">Loading webhook health…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Could not load webhook health: {error}
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="webhook-health-empty">
        <div className="flex items-start gap-3">
          <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-100 text-slate-400 flex-shrink-0">
            <Activity className="size-5" strokeWidth={1.7} />
          </span>
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight">No webhook configured</h3>
            <p className="text-[13px] text-slate-500 mt-1">Once you configure a webhook URL below and fire your first test delivery, this dashboard will populate with real-time health metrics.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="webhook-health">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight inline-flex items-center gap-2">
            <Activity className="size-4 text-[#0055FF]" strokeWidth={2.2} /> Webhook health
          </h3>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Delivery to <span className="font-mono text-slate-700 truncate">{data.webhook_url}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-slate-100 p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setWin(w.id)}
                className={`px-3 py-1 text-[12px] rounded-md transition-colors ${win === w.id ? "bg-white text-slate-900 shadow-sm font-medium" : "text-slate-500 hover:text-slate-900"}`}
                data-testid={`webhook-health-window-${w.id}`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-full h-8" data-testid="webhook-health-refresh">
            <RefreshCw className={`size-3.5 ${busy ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="Success rate"
          value={stats?.ok_pct === null ? "—" : `${stats?.ok_pct ?? 0}%`}
          tone={
            stats?.ok_pct == null ? "muted" :
            stats.ok_pct >= 98    ? "emerald" :
            stats.ok_pct >= 90    ? "amber"   :
            "red"
          }
          Icon={CheckCircle2}
          testid="kpi-success-rate"
        />
        <Tile
          label="p50 latency"
          value={stats?.p50_ms ? `${stats.p50_ms}ms` : "—"}
          tone={stats?.p50_ms && stats.p50_ms > 2000 ? "amber" : "default"}
          Icon={Clock}
          testid="kpi-p50"
        />
        <Tile
          label="p95 latency"
          value={stats?.p95_ms ? `${stats.p95_ms}ms` : "—"}
          tone={stats?.p95_ms && stats.p95_ms > 4500 ? "amber" : "default"}
          Icon={Zap}
          testid="kpi-p95"
        />
        <Tile
          label="Attempts"
          value={stats?.total?.toLocaleString() || "0"}
          sub={stats?.fail ? `${stats.fail} failed` : null}
          Icon={BarChart2}
          testid="kpi-attempts"
        />
      </div>

      {/* Daily sparkline + status breakdown */}
      <div className="grid lg:grid-cols-[1fr_320px] gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500 font-semibold">14-day delivery rate</div>
              <div className="text-[13px] text-slate-700 mt-0.5">Daily success % over the last fortnight</div>
            </div>
          </div>
          <DailySparkline daily={daily} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500 font-semibold mb-3">Status codes ({WINDOWS.find(w => w.id === win).label.toLowerCase()})</div>
          {hasData ? (
            <ul className="space-y-1.5">
              {Object.entries(stats.by_status || {}).sort((a,b) => b[1] - a[1]).map(([code, count]) => {
                const total = stats.total || 1;
                const pct = (count / total) * 100;
                const isOk = code.startsWith("2");
                return (
                  <li key={code} className="text-[13px]">
                    <div className="flex items-center justify-between">
                      <span className={`font-mono font-semibold ${isOk ? "text-emerald-700" : code === "ERR" ? "text-amber-700" : "text-rose-700"}`}>{code}</span>
                      <span className="text-slate-600 tabular-nums">{count} <span className="text-slate-400">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${isOk ? "bg-emerald-500" : code === "ERR" ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-[13px] text-slate-500 py-2">No attempts in this window.</div>
          )}
        </div>
      </div>

      {/* Recent failures */}
      {data.failures?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-5" data-testid="webhook-failures">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-grid place-items-center size-7 rounded-md bg-rose-500/15 text-rose-700">
              <AlertTriangle className="size-3.5" strokeWidth={2.2} />
            </span>
            <h4 className="text-[14px] font-semibold text-slate-900">Recent failures ({data.failures.length})</h4>
          </div>
          <ul className="space-y-2">
            {data.failures.map((f, i) => (
              <li key={i} className="flex items-start gap-3 rounded-lg bg-white border border-rose-100 p-3 text-[12.5px]">
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium truncate">
                    {f.error || f.response_snippet?.slice(0, 100) || `HTTP ${f.status_code || "?"}`}
                  </div>
                  <div className="text-slate-500 font-mono text-[11px] mt-0.5">
                    {f.at ? new Date(f.at).toLocaleString("en-GB", { dateStyle:"short", timeStyle:"short" }) : "—"} · {f.duration_ms ?? "?"}ms · {f.status_code || "no response"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone = "default", Icon, testid }) {
  const tones = {
    default:  { ring: "border-slate-200",     bg: "bg-white",            tx: "text-slate-900",   accent: "text-slate-400" },
    muted:    { ring: "border-slate-200",     bg: "bg-white",            tx: "text-slate-400",   accent: "text-slate-300" },
    emerald:  { ring: "border-emerald-200",   bg: "bg-emerald-50/40",    tx: "text-emerald-700", accent: "text-emerald-600" },
    amber:    { ring: "border-amber-200",     bg: "bg-amber-50/40",      tx: "text-amber-700",   accent: "text-amber-600" },
    red:      { ring: "border-rose-200",      bg: "bg-rose-50/40",       tx: "text-rose-700",    accent: "text-rose-600" },
  };
  const t = tones[tone] || tones.default;
  return (
    <div className={`rounded-xl border ${t.ring} ${t.bg} px-4 py-3.5`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon className={`size-3.5 ${t.accent}`} strokeWidth={2} />}
      </div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${t.tx}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function DailySparkline({ daily }) {
  // Render an SVG bar-chart of daily success%, with a tinted bar for "no data" days.
  const W = 600;
  const H = 90;
  const PAD = 8;
  const cols = daily.length || 14;
  const colW = (W - PAD * 2) / cols;
  return (
    <div className="relative" data-testid="daily-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[90px]">
        {/* y-axis 100% line */}
        <line x1={PAD} y1={PAD} x2={W - PAD} y2={PAD} stroke="#E2E8F0" strokeWidth="1" strokeDasharray="3 4" />
        <text x={W - PAD} y={PAD - 2} textAnchor="end" fontSize="9" fill="#94A3B8" fontFamily="JetBrains Mono">100%</text>
        {/* bars */}
        {daily.map((d, i) => {
          const x = PAD + i * colW + 1;
          const w = Math.max(2, colW - 4);
          const hasData = d.total > 0;
          const pct = d.ok_pct == null ? 0 : d.ok_pct;
          const barH = hasData ? Math.max(2, ((H - PAD - 14) * pct) / 100) : 0;
          const y = (H - 14) - barH;
          const colour =
            !hasData      ? "#E2E8F0" :
            pct >= 98     ? "#10B981" :  // emerald
            pct >= 90     ? "#F59E0B" :  // amber
            "#F43F5E";                   // rose
          return (
            <g key={d.date}>
              {!hasData && (
                <rect x={x} y={H - 16} width={w} height={2} fill={colour} rx="1" />
              )}
              {hasData && (
                <rect x={x} y={y} width={w} height={barH} fill={colour} rx="1.5">
                  <title>{d.date} · {d.ok}/{d.total} ok ({pct.toFixed(1)}%)</title>
                </rect>
              )}
            </g>
          );
        })}
        {/* x-axis labels: first/last day */}
        {daily.length > 0 && (
          <>
            <text x={PAD} y={H - 1} fontSize="9" fill="#94A3B8" fontFamily="JetBrains Mono">{daily[0].date.slice(5)}</text>
            <text x={W - PAD} y={H - 1} textAnchor="end" fontSize="9" fill="#94A3B8" fontFamily="JetBrains Mono">{daily[daily.length - 1].date.slice(5)}</text>
          </>
        )}
      </svg>
    </div>
  );
}
